import { positionService, roleService } from '@/api';
import { PositionResource, RoleResource } from '@/resource/model';
import { messageError, translation } from '@/utils';
import { Form, Message } from 'element-ui';
import { CascaderOption } from 'element-ui/types/cascader-panel';
import { Component, Prop, PropSync, Vue } from 'vue-property-decorator';

@Component({
  name: 'add-position'
})
export default class AddPositon extends Vue {
  /**
   * 控制dialog的显示隐藏，默认为false(不显示)
   */
  @PropSync('visible', { default: false }) public syncedVisible!: boolean;
  @Prop({
    required: false,
    type: Object,
    default: () => {
      return null;
    }
  })
  public position!: PositionResource | null;
  @Prop({
    required: false,
    type: Object,
    default: () => {
      return null;
    }
  })
  public parentPosition!: { departmentId: number; id: number } | null;
  @Prop({ required: true, type: Array }) public departments!: Array<CascaderOption>;

  public title = '';
  public positionForm: {
    parentId: number | null;
    posName: string;
    roleId: number | null;
    depId: number | null;
  } = {
    parentId: null,
    posName: '',
    roleId: null,
    depId: null
  };

  public resourceFormRules = {
    depId: [
      {
        required: true,
        validator: (rule: any, value: number, callback: Function): void => {
          if (!value) {
            callback(new Error(translation('position.selectAffiliationDepartment')));
            return;
          }
          callback();
        },
        trigger: 'change'
      }
    ],
    posName: [
      {
        required: true,
        validator: (rule: any, value: string, callback: Function): void => {
          if (!value) {
            callback(new Error(translation('position.inputName')));
            return;
          }
          callback();
        },
        trigger: 'blur'
      },
      {
        validator: (rule: any, value: string, callback: Function): void => {
          positionService
            .checkPositionName(value, this.position?.id)
            .then((isRepeat: boolean) => {
              if (isRepeat) {
                callback(translation('requestError.positionNameRepeat'));
                return;
              }
              callback();
            })
            .catch(error => {
              callback(error);
            });
        },
        trigger: 'blur'
      }
    ],
    roleId: [
      {
        required: true,
        validator: (rule: any, value: number, callback: Function): void => {
          if (!value) {
            callback(new Error(translation('position.selectRole')));
            return;
          }
          callback();
        },
        trigger: 'change'
      }
    ],
    parentId: [
      {
        required: true,
        validator: (rule: any, value: number, callback: Function): void => {
          if (value !== 0 && !value) {
            callback(new Error(translation('position.selectParentPosition')));
            return;
          }
          callback();
        },
        trigger: 'change'
      }
    ]
  };
  public submitLoading = false;
  public positionOptions: Array<PositionResource> = [];
  public rolesOptions: Array<RoleResource> = [];
  private operationType: 'add' | 'edit' = 'add';

  public get departmentOptions(): Array<CascaderOption> {
    return this.departments;
  }

  public dialogOpen(): void {
    this.getRoles();
    if (this.position) {
      this.operationType = 'edit';
      this.title = 'position.editPosition';
      this.getParentPositions(this.position.depId);
      this.$nextTick(() => {
        Object.assign(this.positionForm, this.position);
      });
      return;
    }
    if (this.parentPosition) {
      this.positionForm.parentId = this.parentPosition.id;
      this.positionForm.depId = this.parentPosition.departmentId;
      this.getParentPositions(this.positionForm.depId);
    }
    this.operationType = 'add';
    this.title = 'position.addPosition';
  }

  public dialogClosed(): void {
    this.$emit('dialog-closed');
    (this.$refs.positionForm as Form).resetFields();
    this.resetForm();
  }

  public onSubmit(): void {
    (this.$refs.positionForm as Form).validate(async (valid: boolean) => {
      if (!valid) {
        return;
      }
      this.setLoading(true);
      try {
        if (this.operationType === 'add') {
          await positionService.post({ ...this.positionForm } as any);
          Message.success(translation('operationRes.addSuccess'));
          this.$emit('add-success');
          this.closeDialog();
          return;
        }
        await positionService.put({ ...this.positionForm } as any);
        (this.positionForm as PositionResource).parentName =
          this.positionOptions.find(x => x.id === this.positionForm.parentId)?.posName ?? '';

        (this.positionForm as PositionResource).depName = this.recursionGetSelectedDepName(
          this.positionForm.depId!,
          this.departmentOptions
        );
        Message.success(translation('operationRes.editSuccess'));
        this.$emit('edit-success', this.positionForm);
        this.closeDialog();
      } catch (error) {
        messageError(error);
      } finally {
        this.setLoading(false);
      }
    });
  }

  public async departmentChanged(departmentId: number): Promise<void> {
    if (departmentId) {
      this.getParentPositions(departmentId);
    }
    this.positionForm.parentId = null;
  }

  private async getParentPositions(departmentId: number): Promise<void> {
    try {
      this.positionOptions = (await positionService.getPositionsByDepartment(departmentId)) || [];
      if (this.operationType === 'edit') {
        // 如果是修改，还需要从岗位选项中去除自己
        this.positionOptions = this.positionOptions.filter(x => x.id !== this.position!.id);
      }
      this.positionOptions.push({
        posName: translation('position.topLevel'),
        id: 0
      } as PositionResource);
    } catch (error) {
      messageError(error);
    }
  }

  private async getRoles(): Promise<void> {
    try {
      this.rolesOptions = await roleService.getAllRoles();
    } catch (error) {
      messageError(error);
    }
  }

  /**
   * 递归获取选中的部门名称
   */
  private recursionGetSelectedDepName(departmentId: number, treeOptions: Array<CascaderOption>): string {
    let value = '';
    for (const item of treeOptions) {
      if (item.value === departmentId) {
        value = item.label;
        break;
      }
      if (item.children instanceof Array && item.children.length > 0) {
        const text = this.recursionGetSelectedDepName(departmentId, item.children);
        if (text) return text;
      }
    }
    return value;
  }

  private setLoading(value: boolean): void {
    this.submitLoading = value;
  }

  private closeDialog(): void {
    this.syncedVisible = false;
  }

  private resetForm(): void {
    this.positionForm = {
      parentId: 0,
      posName: '',
      roleId: null,
      depId: null
    };
  }
}
